/**
 * The "new show style" dialog (D32): a name, an optional id, and what to
 * start from — the template (`acp show-style-template`'s piece types, layers,
 * action and panel for the simulated devices) or an empty show style to fill
 * in on its page. The config itself is built by the container from the one
 * template source.
 */
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { entityIdWireSchema } from '@acp/api-contract/primitives.js';

export type ShowStyleRequest = {
  readonly name: string;
  readonly showStyleId: string | null;
  /** False: an empty show style. */
  readonly fromTemplate: boolean;
};

export type CreateShowStyleDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (request: ShowStyleRequest) => void;
};

function CreateShowStyleForm({ onOpenChange, pending, error, onSubmit }: Omit<CreateShowStyleDialogProps, 'open'>) {
  const [name, setName] = useState('');
  const [showStyleId, setShowStyleId] = useState('');
  const [fromTemplate, setFromTemplate] = useState(true);
  const idError = showStyleId.trim() === '' || entityIdWireSchema.safeParse(showStyleId.trim()).success ? null : 'Letters, digits, - and _ only, up to 64.';
  const ready = name.trim() !== '' && idError === null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({ name: name.trim(), showStyleId: showStyleId.trim() === '' ? null : showStyleId.trim(), fromTemplate });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New show style</DialogTitle>
        <DialogDescription>Start from the template — camera, VT, audio, lower-third and fullscreen piece types, a break action and a panel — or from nothing.</DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="show-style-name">Name</Label>
        <Input id="show-style-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} autoFocus />
      </Field>
      <Field invalid={idError !== null}>
        <Label htmlFor="show-style-id">Id (optional)</Label>
        <Input id="show-style-id" value={showStyleId} onChange={(e) => setShowStyleId(e.target.value)} disabled={pending} aria-invalid={idError !== null} placeholder="news" />
        <FieldError>{idError}</FieldError>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={fromTemplate} onChange={(e) => setFromTemplate(e.target.checked)} disabled={pending} data-testid="show-style-from-template" />
        Start from the template (for the simulated devices)
      </label>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="create-show-style-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateShowStyleDialog({ open, ...rest }: CreateShowStyleDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="create-show-style-dialog">
        <CreateShowStyleForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
